/**
 * Stop Status Icon Utilities - Clean, maintainable implementation
 * Refactored from the original Greenmile.Utils.getStopStatusIcon function
 */

import { getResourceTo } from 'react/utils/utils';

export const extractStopData = (stop) => {
  if (!stop) return {};
  if (stop.model?.attributes?.stop) {
    return stop.model.attributes.stop;
  }
  if (stop.model?.attributes) {
    return stop.model.attributes;
  }
  return stop;
};

export const getStopType = (stop) => stop?.stopType?.type ?? null;

export const getNextStopProjectedArrivalTime = (stop, stopType) => {
  const status = getStopStatus(stop, stopType);
  if (stopType !== 'STOP') {
    return null;
  }
  
  const isConfirmedAsNext = stop?.confirmedAsNextStop && 
    typeof stop.confirmedAsNextStop === 'string' && 
    !isNaN(new Date(stop.confirmedAsNextStop).getTime());
    
  return isConfirmedAsNext && status === 'pending' ? stop?.projectedArrival : null;
};

export const generateIconHtml = (nextStopProjectedArrivalTime, className, title, sort, stop) => {
  const iconSort = typeof sort !== 'undefined' ? `<div style="display:none;">${sort}</div>` : '';
  const iconFirstStop = stop?.firstStop ? '<div class=\'iconFirstStop\'></div>' : '';
  const translatedTitle = getResourceTo(title);

  if (nextStopProjectedArrivalTime) {
    return `<div class='stopIconCollection' >
            <div onmouseover='Greenmile.Stop.Util.showNextStopTooltip(event)' onmouseleave='Greenmile.Stop.Util.hideNextStopTooltip(event)' nextstop="${nextStopProjectedArrivalTime}">
            <div class='stopIcon ${className}' title='${translatedTitle}'> ${iconSort}</div>
            <div class='iconNextStop'></div>
            </div>
            ${iconFirstStop}
          </div>`;
  }

  return `<div class='stopIconCollection'>
            <div class='stopIcon ${className}' title='${translatedTitle}'> ${iconSort}</div>
            ${iconFirstStop}
          </div>`;
};

const createIconConfig = (stop, nextStopTime) => (className, title, sort) => generateIconHtml(nextStopTime, className, title, sort, stop);

const getClonedClass = (stop, regular, cloned) => (stop?.cloneDate ? cloned : regular);

export const createAllIconConfigurations = (stop, nextStopProjectedArrivalTime) => {
  const getIcon = createIconConfig(stop, nextStopProjectedArrivalTime);

  return {
    icon_redelivery: getIcon(
      getClonedClass(stop, 'stopRedeliveryStop', 'stopClonedRedeliveryStop'),
      'Greenmile.Stop.IconDescription.redelivery',
      4
    ),
    icon_cancel: getIcon(
      getClonedClass(stop, 'stopCancel', 'stopClonedCancel'),
      'Greenmile.Stop.IconDescription.cancel',
      5
    ),
    icon_undeliverable: getIcon(
      getClonedClass(stop, 'stopUndeliverable', 'stopClonedUndeliverable'),
      'Greenmile.Stop.IconDescription.undeliverable',
      3
    ),
    icon_stop: getIcon(
      getClonedClass(stop, 'stopPending', 'stopClonedPending'),
      'Greenmile.Stop.IconDescription.pending',
      2
    ),
    icon_stop_ok: getIcon(
      getClonedClass(stop, 'stopComplete', 'stopClonedComplete'),
      'Greenmile.Stop.IconDescription.completed',
      1
    ),
    icon_in_progress: getIcon(
      getClonedClass(stop, 'stopsInProgress', 'stopClonedInProgress'),
      'Greenmile.Stop.IconDescription.inProgress',
      0
    ),
    icon_partially_delivered: getIcon(
      getClonedClass(stop, 'stopsPartiallyDelivered', 'stopClonedPartiallyDelivered'),
      'Greenmile.Stop.IconDescription.partiallyDelivered'
    ),
    icon_wait: getIcon('stopWait', 'Greenmile.Stop.IconDescription.wait'),
    icon_overnight: getIcon('stopOvernight', 'Greenmile.Stop.IconDescription.overnight'),
    icon_break: getIcon('stopBreak', 'Greenmile.Stop.IconDescription.break'),
    icon_paid_wait: getIcon('stopPaidWait', 'Greenmile.Stop.IconDescription.paidWait'),
    icon_paid_break: getIcon('stopPaidBreak', 'Greenmile.Stop.IconDescription.paidBreak'),
    icon_paid_overnight: getIcon('stopPaidOvernight', 'Greenmile.Stop.IconDescription.paidOvernight'),
    icon_depot: getIcon('stopDepot', 'Greenmile.Stop.IconDescription.depot')
  };
};

export const getDefaultIconByStopType = (iconConfigs) => ({
  PAID_BREAK: iconConfigs.icon_paid_break,
  UNPAID_BREAK: iconConfigs.icon_break,
  PAID_WAIT: iconConfigs.icon_paid_wait,
  UNPAID_WAIT: iconConfigs.icon_wait,
  PAID_OVERNIGHT: iconConfigs.icon_paid_overnight,
  UNPAID_OVERNIGHT: iconConfigs.icon_overnight,
  DEPOT: iconConfigs.icon_depot,
  STOP: iconConfigs.icon_stop
});

const getStopStatus = (stop, stopType) => {
  if (stop?.redelivered) return 'redelivery';
  if (stop?.canceled) return 'cancel';
  if (stop?.deliveryStatus === 'PARTIALLY_DELIVERED') return 'partially_delivered';
  if (stop?.returned) return 'undeliverable';

  const hasArrival = stop?.actualArrival;
  const hasDeparture = stop?.actualDeparture;

  if (hasArrival && hasDeparture && stopType === 'STOP') return 'completed';
  if (hasArrival && !hasDeparture && stopType !== 'DEPOT') return 'in_progress';

  return 'pending';
};

export const getCorrectIcon = (stop, iconConfigurations, stopType) => {
  const status = getStopStatus(stop, stopType);
  const defaultIconByStopType = getDefaultIconByStopType(iconConfigurations);

  const statusIconMap = {
    redelivery: iconConfigurations.icon_redelivery,
    cancel: iconConfigurations.icon_cancel,
    partially_delivered: iconConfigurations.icon_partially_delivered,
    undeliverable: iconConfigurations.icon_undeliverable,
    completed: iconConfigurations.icon_stop_ok,
    in_progress: iconConfigurations.icon_in_progress,
    pending: defaultIconByStopType[stopType] || defaultIconByStopType.STOP
  };

  return statusIconMap[status];
};
